import { useEffect, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { useThemeContext } from "@/shared/context/theme-context";

// Scopes can nest: route guards render `StatusPage` (which carries its
// own scope) inside `AdminLayout`, and dialogs/sheets portal to <body>. The
// <body>/<html> classes are therefore reference-counted so an inner scope
// unmounting never strips the chrome an outer scope still relies on.
let mountedScopeCount = 0;

const acquireGlobalScopeClasses = () => {
  mountedScopeCount += 1;
  document.body.classList.add("admin-theme");
};

const releaseGlobalScopeClasses = () => {
  mountedScopeCount -= 1;
  if (mountedScopeCount === 0) {
    document.body.classList.remove("admin-theme");
    document.documentElement.classList.remove("dark");
  }
};

type AdminThemeScopeProps = {
  className?: string;
  children: ReactNode;
};

/**
 * Applies the admin design-system token scope while mounted: the `admin-theme`
 * class on this subtree's root and mirrored on `<body>` (Radix portals mount
 * there, outside the subtree), plus the `.dark` class on `<html>` per the
 * stored light/dark preference. `AdminLayout` and the creator-facing scaffolds
 * (`StatusPage`, `CreatorPage`) all render through this scope so every
 * non-marketing surface shares one look and follows the theme preference;
 * marketing surfaces never mount it and stay light.
 */
export const AdminThemeScope = ({ className, children }: AdminThemeScopeProps) => {
  useEffect(() => {
    acquireGlobalScopeClasses();
    return releaseGlobalScopeClasses;
  }, []);

  // Idempotent across nested scopes (they all resolve the same preference);
  // removal on last unmount is owned by the mount effect above.
  const { resolvedTheme } = useThemeContext();
  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
  }, [resolvedTheme]);

  return <div className={cn("admin-theme", className)}>{children}</div>;
};
